import { useState } from "react";
import { isEmpty, map } from "lodash";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faToggleOff, faToggleOn } from "@fortawesome/pro-solid-svg-icons";
import LinkPreview from "../../../../shared/LinkPreview";
import { LinkAttachment as LinkAttachmentType } from "@/@types/activities.ts";

type LinkAttachmentsArgs = {
  links: LinkAttachmentType[];
};

function PlainLinkPreview({ link }: { link: LinkAttachmentType }) {
  const [imageFailed, setImageFailed] = useState(false);
  // Only reserve the image column when the preview image actually loads, so a
  // broken/unreachable image collapses instead of leaving an empty box and
  // indenting the description.
  const hasImage = !!link.image && !imageFailed;

  return (
    <div
      className={classNames("grid grid-cols-1 bg-white rounded mt-2 pb-2", {
        "grid-cols-[auto_1fr]": hasImage,
      })}
    >
      <LinkPreview
        link={{
          ...link,
          image_url: link.image,
          url: link.href,
          description: link.text,
        }}
        onImageError={() => setImageFailed(true)}
      />
    </div>
  );
}

// oEmbed video html is a single `<iframe src="…">`. Extract and validate that
// src so we can render a clean iframe instead of injecting the raw provider
// markup, removing the dangerouslySetInnerHTML XSS sink for the common case
// (issue #5068 hardening). Returns null for non-iframe / non-https payloads.
function embedIframeSrc(html?: string): string | null {
  if (!html || typeof DOMParser === "undefined") return null;
  const src = new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector("iframe")
    ?.getAttribute("src");
  if (!src) return null;
  try {
    return new URL(src, window.location.href).protocol === "https:"
      ? src
      : null;
  } catch {
    return null;
  }
}

// Restores the embedded-media (oEmbed) player for video links such as YouTube
// in posts (issue #5068). The third-party iframe is only loaded after the user
// clicks "show" — a privacy/GDPR opt-in, mirroring the widget EmbedLinkView and
// the legacy activitystream link view that the React migration dropped.
function EmbeddableLinkAttachment({ link }: { link: LinkAttachmentType }) {
  const [showEmbed, setShowEmbed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const embed = link.embed!;
  const hasImage = !!link.image && !imageFailed;
  // The privacy-friendly secure-embed proxy wins; otherwise fall back to the
  // iframe src extracted from the oEmbed html.
  const iframeSrc = link.secure_embed_service_url ?? embedIframeSrc(embed.html);

  if (showEmbed) {
    return (
      <div className="flex flex-col gap-1 bg-white rounded mt-2 p-2">
        {iframeSrc ? (
          <iframe
            className="w-full border-none [&]:max-w-full"
            style={{ minHeight: 250 }}
            src={iframeSrc}
            height={embed.height}
            allow="fullscreen"
          />
        ) : (
          // Non-iframe oEmbed (e.g. script-based players): render the raw html in
          // a sandboxed iframe with an opaque origin, so an injected script is
          // isolated from the tixxt session instead of running in the page.
          <iframe
            className="activity-link-embed w-full border-none"
            style={{ minHeight: 250 }}
            sandbox="allow-scripts allow-popups allow-presentation"
            srcDoc={embed.html}
          />
        )}
        <button
          type="button"
          className="self-start text-sm text-muted"
          onClick={() => setShowEmbed(false)}
        >
          {I18n.t("js.activitystreams.embed.close")}
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[auto_1fr] gap-3 bg-white rounded mt-2 pb-2">
      <button
        type="button"
        title={I18n.t("js.activitystreams.embed.privacy")}
        onClick={() => setShowEmbed(true)}
        className="relative group flex shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0"
      >
        {hasImage ? (
          <img
            src={link.image}
            alt=""
            className="max-w-[150px] rounded object-cover"
            onError={() => setImageFailed(true)}
          />
        ) : (
          embed.provider_icon && (
            <i
              className={classNames("provider-icon fa-3x", embed.provider_icon)}
            />
          )
        )}
        <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-white/80 p-1 text-sm font-medium">
          <FontAwesomeIcon className="group-hover:hidden" icon={faToggleOff} />
          <FontAwesomeIcon
            className="hidden group-hover:inline"
            icon={faToggleOn}
          />
          {I18n.t("js.activitystreams.embed.show")}
        </span>
      </button>
      <LinkPreview
        link={{
          ...link,
          image_url: undefined,
          url: link.href,
          description: link.text,
        }}
      />
    </div>
  );
}

function LinkAttachment({ link }: { link: LinkAttachmentType }) {
  // Only treat a link as embeddable when there is something to actually embed: a
  // secure-embed proxy url or oEmbed html. secure_embed_service_url alone is set
  // on every link on secure-embed networks, so it's gated behind a non-empty
  // embed; an embed with neither html nor a proxy url falls back to the plain
  // preview instead of rendering an empty player (issue #5068).
  const canEmbed =
    !isEmpty(link.embed) &&
    (!!link.secure_embed_service_url || !isEmpty(link.embed?.html));
  return canEmbed ? (
    <EmbeddableLinkAttachment link={link} />
  ) : (
    <PlainLinkPreview link={link} />
  );
}

export default function LinkAttachments({ links }: LinkAttachmentsArgs) {
  return map(links, (link) => <LinkAttachment key={link.id} link={link} />);
}
